'use client';

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type PropsWithChildren,
} from 'react';
import type { ApiError } from '@/client/api/client';
import {
  createProject as createProjectInDb,
  createSegment as createSegmentInDb,
  deleteProject as deleteProjectInDb,
  deleteSegment as deleteSegmentInDb,
  importProject as importProjectInDb,
  saveImportedMediaAssets,
  listProjects,
  listSegmentsByProject,
  updateSegment as updateSegmentInDb,
} from './storage';
import type {
  StoryboardProject,
  StoryboardProjectDraft,
  StoryboardProjectImportPayload,
  StoryboardSegment,
  StoryboardSegmentDraft,
} from './types';
import type { StoryboardMediaImportAsset } from './storage';

export interface StoryboardContextValue {
  loadingProjects: boolean;
  loadingSegments: boolean;
  mutating: boolean;
  projects: StoryboardProject[];
  segments: StoryboardSegment[];
  activeProjectId: string | null;
  activeSegmentId: string | null;
  focusedPreviewEntryIds: Record<string, string | null>;
  error: string | null;
  selectProject: (projectId: string | null) => void;
  selectSegment: (segmentId: string | null) => void;
  refreshProjects: () => Promise<void>;
  createProject: (draft: StoryboardProjectDraft) => Promise<StoryboardProject | null>;
  deleteProject: (projectId: string) => Promise<void>;
  createSegment: (draft: Omit<StoryboardSegmentDraft, 'order'>) => Promise<StoryboardSegment | null>;
  reorderSegments: (segmentId: string, targetIndex: number) => Promise<void>;
  updateSegment: (
    segmentId: string,
    updates: Partial<StoryboardSegment>,
    options?: { silent?: boolean }
  ) => Promise<StoryboardSegment | null>;
  deleteSegment: (segmentId: string) => Promise<void>;
  importProject: (
    payload: StoryboardProjectImportPayload,
    mediaAssets?: StoryboardMediaImportAsset[]
  ) => Promise<StoryboardProject | null>;
  focusPreviewEntry: (segmentId: string, previewId: string | null) => void;
  clearError: () => void;
}

const StoryboardContext = createContext<StoryboardContextValue | undefined>(undefined);

const deriveSegmentClone = (
  segments: StoryboardSegment[],
  inheritFromSegmentId?: string
): Partial<StoryboardSegment> | undefined => {
  if (!inheritFromSegmentId) return undefined;
  const base = segments.find((segment) => segment.segmentId === inheritFromSegmentId);
  if (!base) return undefined;
  const { modelId, prompt, referenceFrame, duration, transition } = base;
  return {
    modelId,
    prompt,
    referenceFrame,
    duration,
    transition,
  };
};

export const StoryboardProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const [loadingProjects, setLoadingProjects] = useState<boolean>(true);
  const [loadingSegments, setLoadingSegments] = useState<boolean>(false);
  const [mutating, setMutating] = useState<boolean>(false);
  const [projects, setProjects] = useState<StoryboardProject[]>([]);
  const [segments, setSegments] = useState<StoryboardSegment[]>([]);
  const [activeProjectId, setActiveProjectId] = useState<string | null>(null);
  const [activeSegmentId, setActiveSegmentId] = useState<string | null>(null);
  const [focusedPreviewEntryIds, setFocusedPreviewEntryIds] = useState<Record<string, string | null>>({});
  const [error, setError] = useState<string | null>(null);
  const initializingRef = useRef<boolean>(false);
  const segmentsRef = useRef<StoryboardSegment[]>([]);

  const handleError = useCallback((err: unknown) => {
    console.log('[handleError] 接收到的错误:', err, '类型:', typeof err);
    const ensureString = (value: unknown): string | undefined => {
      if (value == null) return undefined;
      if (typeof value === 'string') return value;
      try {
        return String(value);
      } catch {
        return undefined;
      }
    };

    const isApiError = (value: unknown): value is ApiError => {
      if (!value || typeof value !== 'object') return false;
      if (!('message' in value)) return false;
      return typeof (value as { message?: unknown }).message === 'string';
    };

    const extractNestedMessage = (value: unknown): string | undefined => {
      if (!value || typeof value !== 'object') return undefined;
      const nestedContainer = (value as { error?: unknown }).error;
      if (!nestedContainer || typeof nestedContainer !== 'object') return undefined;
      const nested = nestedContainer as { message?: unknown; message_zh?: unknown };
      const messageZh = ensureString(nested.message_zh);
      if (messageZh !== undefined) return messageZh;
      return ensureString(nested.message);
    };

    const extractApiBodyMessage = (value: unknown): string | undefined => {
      if (!isApiError(value)) return undefined;
      const body = value.body;
      if (!body || typeof body !== 'object') return undefined;
      const nested = extractNestedMessage(body);
      if (nested !== undefined) return nested;
      const messageZh = ensureString((body as { message_zh?: unknown }).message_zh);
      if (messageZh !== undefined) return messageZh;
      return ensureString((body as { message?: unknown }).message);
    };

    const nestedMessage = extractApiBodyMessage(err) ?? extractNestedMessage(err);
    let message = nestedMessage ?? 'Storyboard operation failed';

    if (!nestedMessage) {
      if (err instanceof Error) {
        message = err.message || message;
      } else if (typeof err === 'string') {
        message = err;
      } else if (err && typeof err === 'object') {
        const objectWithMessage = err as { message?: unknown };
        if (typeof objectWithMessage.message === 'string') {
          message = objectWithMessage.message;
        } else if (objectWithMessage.message != null) {
          message = String(objectWithMessage.message);
        } else {
          try {
            message = JSON.stringify(err);
          } catch {
            message = message;
          }
        }
      } else if (typeof err === 'number' || typeof err === 'boolean') {
        message = String(err);
      }
    }
    setError(message);
    console.error('[storyboard]', err);
  }, []);

  const refreshProjects = useCallback(async () => {
    setLoadingProjects(true);
    try {
      const fetched = await listProjects();
      setProjects(fetched);
      setLoadingProjects(false);
      if (!fetched.length) {
        setActiveProjectId(null);
        setSegments([]);
        setActiveSegmentId(null);
        return;
      }
      setActiveProjectId((current) => {
        if (current && fetched.some((project) => project.projectId === current)) {
          return current;
        }
        return fetched[0].projectId;
      });
    } catch (err) {
      setLoadingProjects(false);
      handleError(err);
    }
  }, [handleError]);

  const loadSegments = useCallback(
    async (projectId: string | null) => {
      if (!projectId) {
        setSegments([]);
        setActiveSegmentId(null);
        return;
      }
      setLoadingSegments(true);
      try {
        const fetched = await listSegmentsByProject(projectId);
        setSegments(fetched);
        setActiveSegmentId((current) => {
          if (current && fetched.some((segment) => segment.segmentId === current)) {
            return current;
          }
          return fetched.length ? fetched[0].segmentId : null;
        });
      } catch (err) {
        handleError(err);
      } finally {
        setLoadingSegments(false);
      }
    },
    [handleError]
  );

  useEffect(() => {
    if (initializingRef.current) return;
    initializingRef.current = true;
    refreshProjects().finally(() => {
      initializingRef.current = false;
    });
  }, [refreshProjects]);

  useEffect(() => {
    loadSegments(activeProjectId);
  }, [activeProjectId, loadSegments]);

  useEffect(() => {
    segmentsRef.current = segments;
  }, [segments]);

  useEffect(() => {
    setFocusedPreviewEntryIds((prev) => {
      let mutated = false;
      const next: Record<string, string | null> = {};
      for (const segment of segments) {
        const prevFocused = Object.prototype.hasOwnProperty.call(prev, segment.segmentId)
          ? prev[segment.segmentId]
          : null;
        const exists =
          prevFocused != null &&
          segment.previewEntries.some((entry) => entry.previewId === prevFocused);
        const nextFocused = exists ? prevFocused : null;
        next[segment.segmentId] = nextFocused;
        if ((prevFocused ?? null) !== nextFocused) {
          mutated = true;
        }
      }
      if (!mutated) {
        const prevKeys = Object.keys(prev);
        const nextKeys = Object.keys(next);
        if (prevKeys.length !== nextKeys.length) {
          mutated = true;
        } else {
          for (const key of prevKeys) {
            if (!Object.prototype.hasOwnProperty.call(next, key)) {
              mutated = true;
              break;
            }
          }
        }
      }
      return mutated ? next : prev;
    });
  }, [segments]);

  const selectProject = useCallback((projectId: string | null) => {
    setActiveProjectId(projectId);
  }, []);

  const selectSegment = useCallback((segmentId: string | null) => {
    setActiveSegmentId(segmentId);
  }, []);

  const focusPreviewEntry = useCallback((segmentId: string, previewId: string | null) => {
    const segment = segmentsRef.current.find((item) => item.segmentId === segmentId);
    if (!segment) {
      return;
    }
    const validPreviewId =
      previewId && segment.previewEntries.some((entry) => entry.previewId === previewId)
        ? previewId
        : null;
    setFocusedPreviewEntryIds((prev) => {
      const current = prev[segmentId] ?? null;
      if (current === validPreviewId) {
        return prev;
      }
      return {
        ...prev,
        [segmentId]: validPreviewId,
      };
    });
  }, []);

  const createProject = useCallback(
    async (draft: StoryboardProjectDraft) => {
      setMutating(true);
      try {
        const project = await createProjectInDb(draft);
        if (project) {
          await refreshProjects();
          setActiveProjectId(project.projectId);
        }
        setMutating(false);
        return project;
      } catch (err) {
        setMutating(false);
        handleError(err);
        return null;
      }
    },
    [handleError, refreshProjects]
  );

  const deleteProject = useCallback(
    async (projectId: string) => {
      setMutating(true);
      try {
        await deleteProjectInDb(projectId);
        await refreshProjects();
      } catch (err) {
        handleError(err);
      } finally {
        setMutating(false);
      }
    },
    [handleError, refreshProjects]
  );

  const importProject = useCallback(
    async (payload: StoryboardProjectImportPayload, mediaAssets?: StoryboardMediaImportAsset[]) => {
      setMutating(true);
      try {
        const result = await importProjectInDb(payload);
        if (result) {
          if (mediaAssets?.length) {
            try {
              await saveImportedMediaAssets(result.project.projectId, result.previewIdMap, mediaAssets);
            } catch (assetError) {
              console.error('[storyboard] 导入视频资源失败', assetError);
            }
          }
          await refreshProjects();
          setActiveProjectId(result.project.projectId);
          setSegments(result.segments);
          setActiveSegmentId(result.segments.length ? result.segments[0].segmentId : null);
        }
        return result?.project ?? null;
      } catch (err) {
        handleError(err);
        return null;
      } finally {
        setMutating(false);
      }
    },
    [handleError, refreshProjects]
  );

  const createSegment = useCallback(
    async (draft: Omit<StoryboardSegmentDraft, 'order'>) => {
      const projectId = draft.projectId;
      if (!projectId) {
        handleError(new Error('Cannot create segment without project'));
        return null;
      }
      const currentSegments = segmentsRef.current;
      const nextOrder = currentSegments.reduce((acc, segment) => Math.max(acc, segment.order), -1) + 1;
      setMutating(true);
      try {
        const cloneOverrides = deriveSegmentClone(currentSegments, draft.inheritFromSegmentId);
        const newSegment = await createSegmentInDb(
          { ...draft, order: nextOrder },
          cloneOverrides
        );
        if (newSegment) {
          setSegments((prev) =>
            [...prev, newSegment].sort((a, b) => a.order - b.order)
          );
          setActiveSegmentId(newSegment.segmentId);
        }
        return newSegment;
      } catch (err) {
        handleError(err);
        return null;
      } finally {
        setMutating(false);
      }
    },
    [handleError]
  );

  const updateSegment = useCallback(
    async (
      segmentId: string,
      updates: Partial<StoryboardSegment>,
      options?: { silent?: boolean }
    ) => {
      const current = segmentsRef.current.find((segment) => segment.segmentId === segmentId);
      if (!current) return null;
      const silent = options?.silent ?? false;
      if (!silent) {
        setMutating(true);
      }
      try {
        const nextSegment = await updateSegmentInDb({ ...current, ...updates });
        if (nextSegment) {
          setSegments((prev) =>
            prev
              .map((segment) => (segment.segmentId === nextSegment.segmentId ? nextSegment : segment))
              .sort((a, b) => a.order - b.order)
          );
        }
        return nextSegment;
      } catch (err) {
        handleError(err);
        return null;
      } finally {
        if (!silent) {
          setMutating(false);
        }
      }
    },
    [handleError]
  );

  const deleteSegment = useCallback(
    async (segmentId: string) => {
      setMutating(true);
      try {
        await deleteSegmentInDb(segmentId);
        let nextSegments: StoryboardSegment[] = [];
        setSegments((prev) => {
          nextSegments = prev.filter((segment) => segment.segmentId !== segmentId);
          return nextSegments;
        });
        setActiveSegmentId((current) => {
          if (current === segmentId) {
            return nextSegments.length ? nextSegments[0].segmentId : null;
          }
          return current;
        });
      } catch (err) {
        handleError(err);
      } finally {
        setMutating(false);
      }
    },
    [handleError]
  );

  const reorderSegments = useCallback(
    async (segmentId: string, targetIndex: number) => {
      const ordered = [...segmentsRef.current].sort((a, b) => a.order - b.order);
      const fromIndex = ordered.findIndex((segment) => segment.segmentId === segmentId);
      if (fromIndex < 0) {
        return;
      }
      const boundedTarget = Math.min(Math.max(targetIndex, 0), ordered.length - 1);
      if (fromIndex === boundedTarget) {
        return;
      }

      const nextOrdered = [...ordered];
      const [moved] = nextOrdered.splice(fromIndex, 1);
      nextOrdered.splice(boundedTarget, 0, moved);

      const previousSegments = segmentsRef.current;
      const previousOrderMap = new Map(previousSegments.map((segment) => [segment.segmentId, segment.order]));
      const reorderedWithOrder = nextOrdered.map((segment, index) =>
        segment.order === index ? segment : { ...segment, order: index }
      );

      setSegments(reorderedWithOrder);
      segmentsRef.current = reorderedWithOrder;
      setMutating(true);

      const changed = reorderedWithOrder.filter(
        (segment) => previousOrderMap.get(segment.segmentId) !== segment.order
      );

      try {
        if (changed.length) {
          const updated = await Promise.all(
            changed.map(async (segment) => updateSegmentInDb(segment))
          );
          const updatedMap = new Map(
            updated
              .filter((segment): segment is StoryboardSegment => Boolean(segment))
              .map((segment) => [segment.segmentId, segment])
          );
          if (updatedMap.size) {
            setSegments((current) =>
              current.map((segment) => updatedMap.get(segment.segmentId) ?? segment)
            );
            segmentsRef.current = segmentsRef.current.map(
              (segment) => updatedMap.get(segment.segmentId) ?? segment
            );
          }
        }
      } catch (err) {
        setSegments(previousSegments);
        segmentsRef.current = previousSegments;
        handleError(err);
      } finally {
        setMutating(false);
      }
    },
    [handleError]
  );

  const clearError = useCallback(() => setError(null), []);

  const value = useMemo<StoryboardContextValue>(
    () => ({
      loadingProjects,
      loadingSegments,
      mutating,
      projects,
      segments,
      activeProjectId,
      activeSegmentId,
      focusedPreviewEntryIds,
      error,
      selectProject,
      selectSegment,
      refreshProjects,
      createProject,
      deleteProject,
      importProject,
      focusPreviewEntry,
      createSegment,
      reorderSegments,
      updateSegment,
      deleteSegment,
      clearError,
    }),
    [
      activeProjectId,
      activeSegmentId,
      createProject,
      createSegment,
      deleteProject,
      deleteSegment,
      focusPreviewEntry,
      error,
      focusedPreviewEntryIds,
      loadingProjects,
      loadingSegments,
      mutating,
      projects,
      refreshProjects,
      segments,
      selectProject,
      selectSegment,
      reorderSegments,
      updateSegment,
      importProject,
      clearError,
    ]
  );

  return <StoryboardContext.Provider value={value}>{children}</StoryboardContext.Provider>;
};

export const useStoryboard = (): StoryboardContextValue => {
  const context = useContext(StoryboardContext);
  if (!context) {
    throw new Error('useStoryboard must be used within a StoryboardProvider');
  }
  return context;
};
